import { Controller } from 'stimulus'

// Cloned from frontend/src/px/controllers/bottom-sheet-modal_controller.ts to
// keep balance independent of the px namespace. CSS hooks use the `bpx-`
// prefix (see frontend/src/balance/base.css).
const modalVisibleClass = 'bpx-bottom-sheet-visible'
const VELOCITY_THRESHOLD = 0.5 // pixels/ms

const OVERLAY_STYLES = {
  visible: { opacity: '1', transition: 'opacity 0.5s ease-in-out' },
  hidden: { opacity: '0', transition: 'opacity 0.5s ease-in-out' },
}

const INTERACTIVE_TAGS = ['A', 'BUTTON', 'INPUT', 'LABEL', 'SELECT', 'TEXTAREA']

const CLOSE_MODAL_EVENT = 'onclosebottomsheetmodal'

export default class extends Controller {
  declare modalTarget: HTMLElement
  declare cardTarget: HTMLElement
  declare dragHandleTarget: HTMLElement
  declare hasAutofocusTarget: boolean
  declare autofocusTarget: HTMLElement
  static targets = ['modal', 'card', 'dragHandle', 'autofocus']

  declare openImmediatelyValue: boolean
  static values = { openImmediately: Boolean }

  dragStart: number | null = null
  lastY: number | null = null
  lastTime: number | null = Date.now()
  isDragging = false

  connect(): void {
    this.resetModalState()
    this.element.addEventListener(CLOSE_MODAL_EVENT, () => this.dismissModal())

    this.handleClickOutsideCard = this.handleClickOutsideCard.bind(this)
    this.modalTarget.addEventListener('click', this.handleClickOutsideCard)

    if (this.openImmediatelyValue) {
      this.showModal()
    }
  }

  disconnect() {
    this.modalTarget.removeEventListener(CLOSE_MODAL_EVENT, this.dismissModal)
    this.modalTarget.removeEventListener('click', this.handleClickOutsideCard)
  }

  private resetModalState(): void {
    if (!this.cardTarget) return
    this.cardTarget.style.transform = 'translateX(0) translateY(100%)'
    this.modalTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)'
    this.modalTarget.style.opacity = '0'
    this.modalTarget.style.pointerEvents = 'none'
    this.modalTarget.classList.remove(modalVisibleClass)
  }

  private interactiveElements(): HTMLElement[] {
    return Array.from(
      this.modalTarget.querySelectorAll(INTERACTIVE_TAGS.join(',')),
    )
  }

  showModal() {
    if (this.hasAutofocusTarget) {
      this.autofocusTarget.focus()
    }

    this.modalTarget.style.opacity = '1'
    this.modalTarget.style.pointerEvents = 'auto'

    requestAnimationFrame(() => {
      this.modalTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
      this.cardTarget.style.transform = 'translateX(0) translateY(0)'
      this.modalTarget.classList.add(modalVisibleClass)
    })
  }

  dismissModal() {
    Object.assign(this.modalTarget.style, OVERLAY_STYLES.hidden)

    const handleTransitionEnd: EventListener = () => {
      this.resetModalState()
    }

    this.cardTarget.addEventListener('transitionend', handleTransitionEnd, {
      once: true,
    })

    requestAnimationFrame(() => {
      this.cardTarget.style.transform = 'translateX(0) translateY(100%)'
      this.modalTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)'
    })
  }

  handleClickOutsideCard(event: MouseEvent) {
    if (this.modalTarget.hidden) {
      return
    }

    if (
      event.target instanceof window.Node &&
      !this.cardTarget.contains(event.target)
    ) {
      this.dismissModal()
    }
  }

  shouldHandleDrag(event: Event): boolean {
    const target = event.target as HTMLElement | null
    if (!target) return false

    const isTargetInteractive = this.interactiveElements().some((element) =>
      element.contains(target),
    )
    if (isTargetInteractive) {
      return false
    }

    const isDragHandle = this.dragHandleTarget.contains(target)
    const isCard = this.cardTarget.contains(target)

    return isDragHandle || isCard
  }

  handleTouchStart(e: TouchEvent): void {
    if (!this.shouldHandleDrag(e)) return
    e.preventDefault()
    this.dragStart = e.touches[0].clientY
    this.lastY = this.dragStart
    this.lastTime = Date.now()
    this.isDragging = true
  }

  handleMouseDown(e: MouseEvent) {
    if (!this.shouldHandleDrag(e)) return
    e.preventDefault()
    this.dragStart = e.clientY
    this.lastY = this.dragStart
    this.lastTime = Date.now()
    this.isDragging = true
  }

  handleMove(clientY: number) {
    if (!this.isDragging || !this.dragStart) return

    const now = Date.now()

    const velocity =
      this.lastY && this.lastTime
        ? (clientY - this.lastY) / (now - this.lastTime)
        : 0
    this.lastY = clientY
    this.lastTime = now

    const delta = Math.max(0, clientY - this.dragStart)
    const opacity = Math.max(0, 1 - delta / this.cardTarget.offsetHeight)

    requestAnimationFrame(() => {
      this.cardTarget.style.transform = `translateX(0) translateY(${delta}px)`
      this.modalTarget.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.5})`
    })

    if (velocity > VELOCITY_THRESHOLD) {
      this.dismissModal()
      this.isDragging = false
      this.dragStart = null
    }
  }

  handleTouchMove(e: TouchEvent) {
    if (!this.isDragging) return
    e.preventDefault()
    this.handleMove(e.touches[0].clientY)
  }

  handleMouseMove(e: MouseEvent) {
    if (!this.isDragging) return
    e.preventDefault()
    this.handleMove(e.clientY)
  }

  handleEnd() {
    if (!this.isDragging) return

    const matrix = new DOMMatrix(getComputedStyle(this.cardTarget).transform)
    const currentY = matrix.m42

    if (currentY > this.cardTarget.offsetHeight * 0.4) {
      this.dismissModal()
    } else {
      this.cardTarget.style.transform = 'translateX(0) translateY(0)'
      this.modalTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
    }

    this.isDragging = false
    this.dragStart = null
    this.lastY = null
    this.lastTime = null
  }

  handleTouchEnd(e: TouchEvent) {
    if (!this.isDragging) return
    e.preventDefault()
    this.handleEnd()
  }

  handleMouseUp(e: MouseEvent) {
    if (!this.isDragging) return
    e.preventDefault()
    this.handleEnd()
  }
}
